/**
 * SelecteurAlerte — rattacher une communication à une alerte.
 *
 * Le rattachement (`alerte_id`, migration 212) est ce qui permet au journal de
 * l'incident d'agréger les messages, newsletters et campagnes SMS émis pendant
 * un événement. Sans lui, le journal ne montre que les envois automatiques de
 * l'alerte — et le document censé prouver l'action municipale est incomplet.
 *
 * Le sélecteur existait déjà, mais dans un seul écran : la messagerie interne.
 * Ce composant l'extrait pour le poser aussi sur le message GRC sortant, la
 * newsletter et la campagne SMS.
 *
 * Depuis la migration 234 la liste couvre les quatre niveaux : une info du
 * lundi peut appartenir au même incident que l'alerte du mardi.
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

export interface AlerteRattachable {
  id: string;
  titre: string;
  niveau: string;
  date: string | null;
  incidentReference: string | null;
}

const PASTILLE: Record<string, string> = {
  crise: '🔴 Crise',
  alerte: '🟠 Alerte',
  avis: '🟡 Avis',
  info: '🔵 Info',
};

const SELECT_CLS =
  'mt-1 h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg';

export function SelecteurAlerte({
  value,
  onChange,
  id = 'rattachement-alerte',
  label = 'Rattacher à une alerte (optionnel)',
}: {
  value: string;
  onChange: (alerteId: string) => void;
  id?: string;
  label?: string;
}) {
  const [alertes, setAlertes] = useState<AlerteRattachable[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        setAlertes((await api.grc.alertes.rattachables()).alertes as AlerteRattachable[]);
      } catch {
        /* best-effort : le rattachement est un confort, pas un prérequis */
      }
    })();
  }, []);

  // Rien à proposer : on n'affiche pas un sélecteur vide.
  if (alertes.length === 0) return null;

  return (
    <div className="mt-3">
      <label htmlFor={id} className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={SELECT_CLS}
      >
        <option value="">— Aucune —</option>
        {alertes.map((a) => (
          <option key={a.id} value={a.id}>
            {PASTILLE[a.niveau] ?? a.niveau} · {a.titre}
            {a.incidentReference ? ` (${a.incidentReference})` : ''}
          </option>
        ))}
      </select>
      {value && (
        <p className="mt-1 text-2xs text-ink-muted">
          Cet envoi apparaîtra dans le journal de l'incident.
        </p>
      )}
    </div>
  );
}
